import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Icon } from '@codex/icons';
import { Close } from '@codex-brightworld/organisms';
import uiLocale from '../../../../../utils/UILocale.js';
import ActionButtons from '../ActionButtons/ActionButtons';
import AttachmentsListHeader from '../AttachmentsListHeader/AttachmentsListHeader';
import AttachmentBlock from '../AttachmentBlock/AttachmentBlock';
import { AddToBasketErrorMessage } from '../AddToBasketErrorMessage/AddToBasketErrorMessage.jsx';

const translations = {
  failedToUpdateBasket: uiLocale.getString('component.psa.failed_to_update_basket') ||
  'Failed to update basket',
  pleaseTryAgain: uiLocale.getString('component.psa.please_try_again') ||
  'Please try again'
};

const StyledContainer = styled.div`
  position: relative;
  width: 440px;
  max-width: 440px;
  height: 100%;
  display: flex;
  flex-direction: column;
  
  @media ${(props) => props.theme.breakpoints.mobile} {
    width: 100%;
  }
`;

const TopBlockWrapper = styled.div`
  padding: 20px 20px 4px 20px;
  background: #fff;
  position: relative;

  @media ${(props) => props.theme.breakpoints.mobile} {
    padding: 10px 10px 4px 10px;
  }
`;

const ScrollWrapper = styled.div`
  background-color: ${(props) => props.theme.colors.shades[6]};
  padding: 10px 10px 0 10px;
  overflow-y: scroll;
  
  @media ${(props) => props.theme.breakpoints.mobile} {
    padding: 10px 5px;
  }
`;

const BorderWrapper = styled.div`
  background-color: #f2f2f2;
  overflow: hidden;
  padding: 10px;
  
  @media ${(props) => props.theme.breakpoints.mobile} {
    padding: 10px 5px;
  }
`;

const AttachmentWrapper = styled.div`
  background: #fff;
  margin-top: 19px;
  padding: 10px;
  border-radius: 10px;
  border: solid 1px #cdd8df;
  
  &:first-child {
    margin-top: 0;
  }
  
  @media ${(props) => props.theme.breakpoints.mobile} {
    padding: 10px;
  }
`;

const BasketInfoWrapper = styled.div`
  font-size: 16px;
  font-weight: bold;
  font-family: Helvetica;
  padding-bottom: 10px;
`;

const TickIcon = styled(Icon)`
  margin: 3px 7px 0 0;
  
  svg {
    margin-top: -2px;
    padding-right: 5px;
    padding-bottom: 2px;
  }
  && circle {
    stroke: #426E29;
  }
  && path {
    fill: #426E29;
  }
    
  > svg {
    > g {
      /* Hide border of the icon */
      path:nth-child(1) {
        display: none
      }
    }
  }
`;

const StyledClose = styled(Close)`
  position: absolute;
  right: 15px;
  z-index: 1;
  width: 10px;
  height: 10px;
  color: ${({ theme }) => theme.colors.link.default};
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
`;

const BasketInfoColumn = styled.div`
  margin: auto 0;
  padding-left: 5px;
  padding-right: 15px;
`;

const BasketInfo = ({
  basketInfoLabel1,
  basketInfoLabel2Singular,
  basketInfoLabel2Plural,
  attachmentsAddedToBasketCount
}) => {
  const attachmentsInBasketCount = attachmentsAddedToBasketCount + 1;
  const basketInfoLabel2 = attachmentsAddedToBasketCount > 0
    ? basketInfoLabel2Plural : basketInfoLabel2Singular;
  return (
    <BasketInfoWrapper>
      <Row>
        <TickIcon icon={'ConfirmationCheck'} size={'large'} />
        <BasketInfoColumn>
          <span>
            {basketInfoLabel1}
&nbsp;
          </span>
          <span data-testid={'attachmentsInBasketCount'}>{attachmentsInBasketCount}</span>
          <span>
            {basketInfoLabel2}
          </span>
        </BasketInfoColumn>
      </Row>
    </BasketInfoWrapper>
  );
};

BasketInfo.propTypes = {
  basketInfoLabel1: PropTypes.string.isRequired,
  basketInfoLabel2Singular: PropTypes.string.isRequired,
  basketInfoLabel2Plural: PropTypes.string.isRequired,
  attachmentsAddedToBasketCount: PropTypes.number.isRequired
};

const WrapperVariant2 = (props) => {

  const {
    productID,
    attachments,
    attachmentsAddedToBasket,
    attachmentsLabel,
    continueToBasketButtonLabel,
    continueShoppingButtonLabel,
    addAttachmentInBasketLabel,
    continueToBasketButtonAction,
    continueShoppingButtonAction,
    addAttachmentInBasketInProgressLabel,
    attachmentAddedInBasketLabel,
    basketInfoLabel1,
    basketInfoLabel2Singular,
    basketInfoLabel2Plural,
    onAddToBasket,
    onAddToBasketSuccess,
    onAddToBasketFailure,
    productIdForFailedAddToBasket,
    psaWhenBoughtWithEnabled
  } = props;

  const parentContainerRef = useRef(null);
  const failedBasketText = () => {
    const { failedToUpdateBasket, pleaseTryAgain } = translations;
    return (
      <AddToBasketErrorMessage
        failedToUpdateBasket={failedToUpdateBasket}
        pleaseTryAgain={pleaseTryAgain}
      />
    );
  };

  return (
    <>
      <StyledContainer ref={parentContainerRef}>
        <StyledClose onClose={continueShoppingButtonAction} />
        <TopBlockWrapper>
          <BasketInfo
            basketInfoLabel1={basketInfoLabel1}
            basketInfoLabel2Singular={basketInfoLabel2Singular}
            basketInfoLabel2Plural={basketInfoLabel2Plural}
            attachmentsAddedToBasketCount={attachmentsAddedToBasket.length}
          />
          <ActionButtons
            primaryButtonLabel={continueToBasketButtonLabel}
            primaryButtonOnClick={continueToBasketButtonAction}
            secondaryButtonLabel={continueShoppingButtonLabel}
            secondaryButtonOnClick={continueShoppingButtonAction}
          />
        </TopBlockWrapper>
        <ScrollWrapper>
          <BorderWrapper>
            {attachmentsLabel && <AttachmentsListHeader label={attachmentsLabel} />}
            {attachments.map((item, actual) => (
              <AttachmentWrapper key={item.id}>
                {item.id === productIdForFailedAddToBasket &&
                failedBasketText()}
                {item && (
                <AttachmentBlock
                  productID={productID}
                  attachmentName={item.name}
                  attachmentBrandLabel={item.brandLabel}
                  attachmentImgUrl={item.imageUrl}
                  ratingCount={item.customerReview.number}
                  ratingValue={item.customerReview.averageScore}
                  currentPrice={item.price.amount}
                  wasPrice={item.wasPrice}
                  currencyCode={item.price.currencyCode}
                  addAttachmentInBasketFupid={item.id}
                  addAttachmentInBasketQuantity={1}
                  addAttachmentInBasketLabel={addAttachmentInBasketLabel}
                  inProgressLabel={addAttachmentInBasketInProgressLabel}
                  attachmentAddedInBasketLabel={attachmentAddedInBasketLabel}
                  onAddToBasket={() => onAddToBasket(productID, item.id)}
                  onAddToBasketSuccess={onAddToBasketSuccess}
                  onAddToBasketFailure={onAddToBasketFailure}
                  psaWhenBoughtWithEnabled={psaWhenBoughtWithEnabled}
                />
                )}
              </AttachmentWrapper>
            ))}
          </BorderWrapper>
        </ScrollWrapper>
      </StyledContainer>
    </>
  );

};

WrapperVariant2.propTypes = {
  psaWhenBoughtWithEnabled: PropTypes.bool.isRequired,
  productID: PropTypes.string.isRequired,
  attachments: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    brandLabel: PropTypes.string,
    imageUrl: PropTypes.string,
    price: PropTypes.shape({
      amount: PropTypes.number,
      currencyCode: PropTypes.string
    }),
    wasPrice: PropTypes.shape({
      amount: PropTypes.number,
      discountAmount: PropTypes.number
    }),
    customerReview: PropTypes.shape({
      averageScore: PropTypes.number,
      number: PropTypes.number
    })
  })).isRequired,
  attachmentsAddedToBasket: PropTypes.arrayOf(PropTypes.string).isRequired,
  attachmentsLabel: PropTypes.string.isRequired,
  continueToBasketButtonLabel: PropTypes.string.isRequired,
  continueShoppingButtonLabel: PropTypes.string.isRequired,
  addAttachmentInBasketLabel: PropTypes.string.isRequired,
  addAttachmentInBasketInProgressLabel: PropTypes.string.isRequired,
  attachmentAddedInBasketLabel: PropTypes.string.isRequired,
  basketInfoLabel1: PropTypes.string.isRequired,
  basketInfoLabel2Singular: PropTypes.string.isRequired,
  basketInfoLabel2Plural: PropTypes.string.isRequired,
  continueToBasketButtonAction: PropTypes.func.isRequired,
  continueShoppingButtonAction: PropTypes.func.isRequired,
  onAddToBasket: PropTypes.func,
  onAddToBasketSuccess: PropTypes.func,
  onAddToBasketFailure: PropTypes.func,
  productIdForFailedAddToBasket: PropTypes.string
};

WrapperVariant2.defaultProps = {
  productIdForFailedAddToBasket: '',
  onAddToBasket: () => {},
  onAddToBasketSuccess: () => {},
  onAddToBasketFailure: () => {}
};

export default WrapperVariant2;
